import { AuthUserPermission, FieldModel, ProgramStatus, generatePath } from '@cibo/core'
import { PaginatedFieldsContext, useLandManagement } from '@cibo/landmanager'
import { usePermissions } from '@cibo/profile'
import {
  useForceCommitField,
  useGrowerPrograms,
  useProgram,
  useProgramEngagement,
  usePrograms,
} from '@cibo/programs'
import { Divider, downloadTextFile } from '@cibo/ui'
import AddchartRounded from '@mui/icons-material/AddchartRounded'
import AgricultureIcon from '@mui/icons-material/Agriculture'
import AssessmentRounded from '@mui/icons-material/AssessmentRounded'
import BugReportRounded from '@mui/icons-material/BugReportRounded'
import DeleteRounded from '@mui/icons-material/DeleteRounded'
import EditRounded from '@mui/icons-material/EditRounded'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import GestureRounded from '@mui/icons-material/GestureRounded'
import PersonAddRounded from '@mui/icons-material/PersonAddRounded'
import RuleRounded from '@mui/icons-material/RuleRounded'
import Undo from '@mui/icons-material/Undo'
import { Box, Skeleton } from '@mui/material'
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridRowId,
  GridRowParams,
} from '@mui/x-data-grid-pro'
import { ENROLLMENT_ROUTES, LAND_MANAGER_ROUTES, PROGRAM_ROUTES } from 'navigation/types/routes'
import { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ENGAGEMENT_PATHS } from '../../navigation/types/engagementRoutes'

/**
 * @todo
 * check and remove this as soon as the MUI GridActionsCellItem typings are updated
 */
const requiredProps = {} as {
  nonce: any
  onResize: any
  onResizeCapture: any
}

export const useFieldTableActionsColumn = ({
  hideFinishEnrollment,
  onEditMeta,
  programId,
  reviewInformationPath,
}: {
  hideFinishEnrollment?: boolean
  onEditMeta: (resourceId: GridRowId) => void
  programId?: string
  reviewInformationPath?: (fieldModel: FieldModel) => undefined | string
}) => {
  const { t } = useTranslation('impact/hooks/useFieldTableActionsColumn')
  const { can } = usePermissions()
  const { setAssignFieldsModal, openAssignFarmDialog } = useLandManagement()
  const { onDelete } = useContext(PaginatedFieldsContext)
  const { onUnenroll } = useGrowerPrograms()
  const programs = usePrograms()
  const forceCommitField = useForceCommitField()

  const showProfile = programs.data?.some(program => program.hasProfile())
  const programEngageType = programs.data?.find(program => program.programId === programId)
    ?.engageType

  const viewAllDetailsAction = (params: GridRowParams<FieldModel>) =>
    can({
      useAny: [AuthUserPermission.DEBUG_USER, AuthUserPermission.FIELD_VIEW_DETAIL_REPORT],
    }) ? (
      <GridActionsCellItem
        // @ts-ignore - component works just fine but is not typed
        component={Link}
        to={generatePath(LAND_MANAGER_ROUTES.DETAIL, { resourceId: params.row.id })}
        icon={
          can({
            useAny: [AuthUserPermission.DEBUG_USER],
            unlessAny: [AuthUserPermission.FIELD_VIEW_DETAIL_REPORT],
          }) ? (
            <BugReportRounded color="error" />
          ) : (
            <AssessmentRounded />
          )
        }
        label={t('fieldDetails')}
        showInMenu
      />
    ) : (
      undefined
    )

  return useMemo(
    () => ({
      field: 'actions',
      type: 'actions',
      width: 24,
      getActions: (params: GridRowParams<FieldModel>) => {
        const programStatuses = params.row.programStatuses?.filter(s =>
          !!programId ? s.programId === programId && s.workflow : s.workflow
        )

        const actions = [
          viewAllDetailsAction(params),
          !hideFinishEnrollment && !!programStatuses?.length
            ? programStatuses
                ?.map(programStatus => (
                  <ResumeEnrollmentButton programStatus={programStatus} showInMenu />
                ))
                .filter(v => !!v)
            : undefined,
          <ProfileButton params={params} showInMenu />,
          !!reviewInformationPath && reviewInformationPath(params.row) ? (
            <GridActionsCellItem
              icon={!!params.row.isMissingProfile ? <AddchartRounded /> : <Box />}
              label={t(`review_${programEngageType}`)}
              // @ts-ignore - component is not included in GridActionsCellItem props but is available
              component={Link}
              to={reviewInformationPath(params.row)}
              disabled={!reviewInformationPath(params.row)}
              showInMenu
            />
          ) : (
            undefined
          ),
          // @ts-ignore showInMenu is necessary but who knows why
          <Divider showInMenu />,
          <GridActionsCellItem
            icon={<GestureRounded />}
            // @ts-ignore - component is not included in GridActionsCellItem props but is available
            component={Link}
            to={generatePath(LAND_MANAGER_ROUTES.DETAIL_EDIT_BOUNDARY, {
              resourceId: params.id as string,
            })}
            label={t('editBoundaries')}
            disabled={!params.row.can(['edit_land'])}
            showInMenu
          />,
          <GridActionsCellItem
            icon={<EditRounded />}
            onClick={() => onEditMeta(params.id)}
            label={t('editNameAndLabel')}
            disabled={!params.row.can(['edit_meta'])}
            showInMenu
            {...requiredProps}
          />,
          params.row.ownerId ? (
            can({ useAny: AuthUserPermission.FIELDS_OWNER_MODIFY }) ? (
              <GridActionsCellItem
                icon={<PersonAddRounded />}
                onClick={() =>
                  setAssignFieldsModal({ open: true, selectedIds: [params.id as string] })
                }
                label={t('assignGrower')}
                disabled={!params.row.can(['field_assign'])}
                showInMenu
                {...requiredProps}
              />
            ) : (
              undefined
            )
          ) : can({ useAny: AuthUserPermission.FIELDS_OWNER_ASSIGN }) ? (
            <GridActionsCellItem
              icon={<PersonAddRounded />}
              onClick={() =>
                setAssignFieldsModal({ open: true, selectedIds: [params.id as string] })
              }
              label={t('assignGrower')}
              disabled={!params.row.can(['field_assign'])}
              showInMenu
              {...requiredProps}
            />
          ) : (
            undefined
          ),

          can({ useAny: AuthUserPermission.FARMS_MANAGE }) ? (
            <GridActionsCellItem
              icon={<AgricultureIcon />}
              onClick={() =>
                openAssignFarmDialog({
                  resourceIds: [params.id as string],
                })
              }
              label={t('assignFarm')}
              disabled={!params.row.can(['edit_meta'])}
              showInMenu
              {...requiredProps}
            />
          ) : (
            undefined
          ),
          can({ useAny: AuthUserPermission.FORCE_UNCOMMIT }) &&
          params.row.participatingPrograms &&
          params.row.participatingPrograms.length > 0 ? (
            <GridActionsCellItem
              onClick={() => onUnenroll(params.row.id)}
              icon={<Undo />}
              label={t('unenroll')}
              showInMenu
              {...requiredProps}
            />
          ) : (
            undefined
          ),

          can({ useAny: AuthUserPermission.DEBUG_USER }) ? (
            <GridActionsCellItem
              onClick={() =>
                downloadTextFile({
                  textContent: JSON.stringify(params.row.geometry, null, 2),
                  filename: params.row.name.replace(/\s/g, '_') + '.json',
                  mimetype: 'application/json',
                })
              }
              icon={<FileDownloadIcon />}
              label={t('exportGeometry')}
              showInMenu
              {...requiredProps}
            />
          ) : (
            undefined
          ),

          <GridActionsCellItem
            icon={<DeleteRounded />}
            onClick={e => onDelete(params.row.resourceId)}
            label={t('delete')}
            disabled={!params.row.can(['delete'])}
            showInMenu
            {...requiredProps}
          />,
        ]
          .filter(v => !!v)
          .flat()

        if (can({ useAny: AuthUserPermission.DEBUG_USER }) && programs.data) {
          // @ts-ignore showInMenu is necessary but who knows why
          actions.push(<Divider showInMenu />)

          programs.data.forEach(({ programId }) =>
            actions.push(
              <GridActionsCellItem
                onClick={() => {
                  forceCommitField.mutateAsync({ fieldId: params.row.id, programId })
                }}
                icon={<BugReportRounded color="error" />}
                data-testid="force-commit-button"
                label={`${t('forceCommit')} ${programId}`}
                showInMenu
                {...requiredProps}
              />
            )
          )
        }

        return actions
      },
    }),
    [can, onEditMeta, programId, forceCommitField, showProfile, programs.dataUpdatedAt]
  )
}

// had to extract out to get programs to resolve
const ResumeEnrollmentButton = ({
  programStatus,
}: { programStatus: ProgramStatus } & Pick<GridActionsCellItemProps, 'showInMenu'>) => {
  const { t } = useTranslation('impact/hooks/useFieldTableActionsColumn')
  const program = useProgram({ programId: programStatus.programId })
  const engagement = useProgramEngagement({
    id: programStatus.rulesVersion === 'v1' ? undefined : programStatus.workflow,
  })
  const programVersion = program.data?.rulesVersion
  const programEngageType = program.data?.engageType

  if (engagement.fetchStatus !== 'idle') {
    return <Skeleton />
  }

  return programVersion === 'v1' ? (
    <GridActionsCellItem
      // @ts-ignore - component works just fine but is not typed
      component={Link}
      to={generatePath(ENROLLMENT_ROUTES.DETAIL, {
        workflowId: programStatus.workflow,
      })}
      icon={<RuleRounded />}
      label={t(`finish_${programEngageType}`, { name: program.data?.content?.fields.title ?? '' })}
      showInMenu
    />
  ) : programVersion === 'v2' && !engagement.data?.isComplete ? (
    <GridActionsCellItem
      // @ts-ignore - component works just fine but is not typed
      component={Link}
      to={generatePath(`${PROGRAM_ROUTES.ENGAGEMENTS}/${ENGAGEMENT_PATHS.ENGAGEMENT}`, {
        programId: programStatus.programId,
        engagementId: programStatus.workflow,
      })}
      icon={<RuleRounded />}
      label={t(`finish_${programEngageType}`, { name: program.data?.content?.fields.title ?? '' })}
      showInMenu
    />
  ) : null
}

// had to extract out to get programs to resolve
const ProfileButton = ({
  params,
}: { params: GridRowParams<FieldModel> } & Pick<GridActionsCellItemProps, 'showInMenu'>) => {
  const { t } = useTranslation('impact/hooks/useFieldTableActionsColumn')
  const programs = usePrograms()
  const showProfile = programs.data?.some(program => program.hasProfile())

  if (programs.isLoading) {
    return <Skeleton />
  }

  return !!showProfile ? (
    <GridActionsCellItem
      icon={!!params.row.isMissingProfile ? <AddchartRounded /> : <Box />}
      label={t('information', {
        context: params.row.isMissingProfile ? 'incomplete' : undefined,
      })}
      // @ts-ignore - component is not included in GridActionsCellItem props but is available
      component={Link}
      to={generatePath(LAND_MANAGER_ROUTES.PROFILE_FIELD, {
        fieldId: params.id as string,
      })}
      showInMenu
    />
  ) : null
}
